import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {Session, SessionTours, SessionUser} from '../models/session.model';
import {UserService} from '@services/user.service';
import {Phone, RegisterModel} from '@shared/models/register.model';
import {AppConfigService} from '@services/app-config.service';
import {DispatcherService, GenericFunctionsUtil, StorageService} from '@core/src/public-api';
import {UnsavedChangesService} from '@services/unsaved-changes.service';
import {TranslateService} from '@ngx-translate/core';
import {PermissionService} from '@services/permission.service';
import {UserProfileAddress, UserProfileLink} from '@shared/models/user-profile.model';
import {RecoverPasswordModel} from '@shared/models/recover-password.model';
import {PasiToast} from '@shared/models/toast.model';
import {CircleAuthorizationRoleChange} from '@shared/models/circle-authorization.model';
import {PatientService} from './patient.service';
import {LinkPatientService} from './link-patient.service';
import {FaqItem} from '@shared/models/faq.model';
import {LoginResendEmail} from '@shared/models/login.model';

@Injectable({
  providedIn: 'root'
})
export class LoginService implements OnDestroy {
  access_token: string;
  session: Session;
  refreshInterval: number;
  AUTHORIZATION_KEY = 'userToken';
  USER_SESSION_KEY = 'userSession';
  USER_SESSION_REFRESH_TIMESTAMP_KEY = 'userSessionRefreshTimestamp';
  emailDefaultMaskMargin = 2;
  phoneDefaultMaskMargin = 2;
  isDisplayingDialogInactivity: boolean;
  DIALOG_INACTIVITY_COUNTDOWN_MILLISECONDS = 60 * 1000;

  constructor(
    private http: HttpClient,
    private router: Router,
    private location: Location,
    private userService: UserService,
    private appConfigService: AppConfigService,
    private permissionService: PermissionService,
    private dispatcher: DispatcherService,
    private unsavedChangesService: UnsavedChangesService,
    private translateService: TranslateService,
    private patientService: PatientService,
    private linkPatientService: LinkPatientService,
    private storageService: StorageService
  ) {
  }

  async login(username: string, password: string, code?: string): Promise<void> {
    try {
      const url = '/api/v1/login';
      const model = {username, password, code};
      const session = await this.http.post<Session>(url, model).toPromise();
      this.isDisplayingDialogInactivity = false;
      this.afterLogin(session);
    } catch (e) {
      this.resetLogin();
      throw e;
    }
  }

  async register(model: RegisterModel, createIfNotLink: boolean): Promise<void> {
    try {
      const url = '/api/v1/user';
      const httpParams = new HttpParams().set('createIfNotLink', createIfNotLink.toString());
      await this.http.post<Session>(url, model, {params: httpParams}).toPromise();
    } catch (e) {
      throw e;
    }
  }

  async refresh(): Promise<void> {
    try {
      const url = '/api/v1/refreshToken';
      const model = {refresh_token: this.getSession()?.refresh_token};
      const session = await this.http.post<Session>(url, model).toPromise();
      this.afterLogin(session);
    } catch (error) {
      this.resetLogin(true);
    }
  }

  // Returns the introspection of the JWT token.
  introspect(): Promise<any> {
    const url = `/api/v1/auth/introspect`;
    return this.http.post(url, {}).toPromise();
  }

  async loginChangingPassword(username: string, password: string, newPassword: string): Promise<void> {
    const url = `/auth/login/changePassword`;
    const model = {username, password, newPassword};
    await this.http.post(url, model).toPromise();
    await this.login(username, newPassword);
  }

  async logout(): Promise<void> {
    const url = '/api/v1/auth/logout';
    await this.http.post(url, {refresh_token: this.getSession()?.refresh_token}).toPromise();
    this.resetLogin(true);
  }

  async revoke(redirectToLogin = true): Promise<void> {
    const url = '/api/v1/auth/revoke';
    await this.http.post(url, {}).toPromise();
    this.resetLogin(redirectToLogin);
  }

  async passwordValidate(password: string): Promise<void> {
    const url = '/api/v1/validatePassword';
    await this.http.post(url, password).toPromise();
  }

  async getCheckEmailByDocument(
    email: string,
    identificationDocument: string,
    identificationDocumentTypeCode: string
  ): Promise<void> {
    const httpParams = new HttpParams()
      .set('email', email)
      .set('identificationDocument', identificationDocument)
      .set('identificationDocumentTypeCode', identificationDocumentTypeCode);
    const url = `/api/v1/user/exist`;
    await this.http.get(url, {params: httpParams}).toPromise();
  }

  async updateMatchExistingPatient(model: UserProfileLink): Promise<Session> {
    model.refreshToken = this.getSession()?.refresh_token;
    const url = '/api/v1/user/patient/link';
    return this.http.put<Session>(url, model).toPromise();
  }

  afterLogin(session: Session): void {
    this.setSession(session);
    this.setAuthorization(session.access_token);
    this.setRefreshTimestamp(session);
  }

  async initRefreshInterval(): Promise<void> {
    this.refreshInterval = setInterval(() => {
      const refreshTimestamp = this.getRefreshTimestamp();
      if (!refreshTimestamp || this.isDisplayingDialogInactivity) {
        return;
      }

      const currentTime = Date.now();
      if (currentTime < (refreshTimestamp - this.DIALOG_INACTIVITY_COUNTDOWN_MILLISECONDS)) {
        return;
      }

      if (currentTime > refreshTimestamp || !this.userService.isUserActive()) {
        this.openDialogInactivity();
      } else {
        this.refresh();
      }
    }, 5000) as any;
  }

  async openDialogInactivity(): Promise<void> {
    this.isDisplayingDialogInactivity = true;
    const acceptDialogFlag = await this.userService.openDialogInactivity();
    this.isDisplayingDialogInactivity = false;
    if (acceptDialogFlag) {
      await this.refresh();
    } else {
      await this.revoke();
    }
  }

  resetLogin(redirectToLogin = false): void {
    this.setSession(null);
    this.setAuthorization(null);
    this.setRefreshTimestamp(null);
    this.appConfigService.resetAppConfig();
    this.permissionService.resetPermissions();
    this.patientService.resetPatientStore();
    this.linkPatientService.setShowLinkPatientWarning(null);
    this.storageService.clearStorage();
    if (redirectToLogin) {
      this.unsavedChangesService.isRedirecting = true;
      this.redirectToLogin();
      this.dispatcher.exec('si-login-reset');
      setTimeout(() => {
        this.unsavedChangesService.isRedirecting = false;
      }, 100);
    }
  }

  redirectToLogin(): void {
    if (this.location.path() === '/login') {
      this.handleLoginRouteReload();
    } else {
      this.router.navigate(['/login']);
    }
  }

  // Sometimes, when reloading the route, if there is any error in the back, the route is reloaded in a loop.
  // To avoid this, we give a margin of time, so that it is only recharged once.
  handleLoginRouteReload(): void {
    const LOGIN_RELOAD_TIMESTAMP = 'loginReloadTimestamp';
    const loginReloadTimestampString = this.storageService.getItem(LOGIN_RELOAD_TIMESTAMP, true);
    const loginReloadTimestamp = loginReloadTimestampString ? parseInt(loginReloadTimestampString, 10) : null;
    if (!loginReloadTimestamp || loginReloadTimestamp < Date.now()) {
      const leewayMilliseconds = 30000;
      const loginReloadTimestampNew = Date.now() + leewayMilliseconds;
      this.storageService.setItem(LOGIN_RELOAD_TIMESTAMP, loginReloadTimestampNew.toString(), true);
      window.location.reload();
    }
  }

  getSession(): Session {
    if (this.session) {
      return this.session;
    }

    const session = this.storageService.getItem(this.USER_SESSION_KEY, true);
    if (!session) {
      return;
    }

    return JSON.parse(session) as Session;
  }

  setSession(session: Session): void {
    if (!session) {
      this.session = null;
      this.storageService.removeItem(this.USER_SESSION_KEY, true);
      this.patientService.setPatientId(null);
      return;
    }

    this.session = session;
    this.storageService.setItem(this.USER_SESSION_KEY, JSON.stringify(session), true);
    this.patientService.setPatientId(session.patientId);
  }

  getAuthorization(): string {
    if (!this.access_token) {
      this.access_token = this.storageService.getItem(this.AUTHORIZATION_KEY, true);
    }

    return this.access_token;
  }

  setAuthorization(access_token: string): void {
    this.access_token = access_token;
    if (!access_token) {
      this.storageService.removeItem(this.AUTHORIZATION_KEY, true);
      return;
    }

    this.storageService.setItem(this.AUTHORIZATION_KEY, access_token, true);
  }

  getRefreshTimestamp(): number {
    const refreshTimestamp = this.storageService.getItem(this.USER_SESSION_REFRESH_TIMESTAMP_KEY, true);
    if (!refreshTimestamp) {
      return;
    }

    return parseInt(refreshTimestamp, 10);
  }

  setRefreshTimestamp(session: Session): void {
    if (!session) {
      this.storageService.removeItem(this.USER_SESSION_REFRESH_TIMESTAMP_KEY, true);
      return;
    }

    let expiresInSeconds = session.expires_in;
    if (session.refresh_expires_in < expiresInSeconds) {
      expiresInSeconds = session.refresh_expires_in;
    }

    const expiresInMilliseconds = expiresInSeconds * 1000;

    // Margin time to refresh the session
    const leewayMilliseconds = 60000;

    // Time when the token has to be refreshed.
    const refreshTimestamp = Date.now() + expiresInMilliseconds - leewayMilliseconds;
    this.storageService.setItem(this.USER_SESSION_REFRESH_TIMESTAMP_KEY, JSON.stringify(refreshTimestamp), true);
  }

  isLoggedIn(reset = false): boolean {
    const refreshTimestamp = this.getRefreshTimestamp();
    if (!this.getAuthorization() || !refreshTimestamp || refreshTimestamp < Date.now()) {
      if (reset) {
        this.resetLogin();
      }

      return false;
    }

    return true;
  }

  getSessionUser(): SessionUser {
    const session = this.getSession();
    return session?.user;
  }

  getPatientId(): number {
    const session = this.getSession();
    return session?.patientId;
  }

  getIsPatient(): boolean {
    const session = this.getSession();
    return !!session?.patientId;
  }

  getPatientAddress(): UserProfileAddress {
    const session = this.getSession();
    return session?.addressDto;
  }

  setPatientAddress(address: UserProfileAddress): void {
    let session = this.getSession();
    session.addressDto = address ?? new UserProfileAddress();
    this.setSession(session);
  }

  getTourCompleted(tourName: string): boolean {
    return this.getSession().toursCompleted?.[tourName] ?? true;
  }

  setTourCompleted(tourName: string, tourCompleted: boolean): void {
    let session = this.getSession();
    session.toursCompleted = session.toursCompleted ?? new SessionTours();
    session.toursCompleted[tourName] = tourCompleted;
    this.setSession(session);
  }

  getPreferredCollectiveId(): number {
    const session = this.getSession();
    return session?.preferredInsuranceId;
  }

  setPreferredCollectiveId(preferredCollectiveId: number): void {
    let session = this.getSession();
    session.preferredInsuranceId = preferredCollectiveId;
    this.setSession(session);
  }

  getPatientPhone(): Phone {
    const session = this.getSession();
    return session?.phone;
  }

  getPasswordPolicies(): Promise<string[]> {
    const url = `/api/v1/passwordPolicies`;
    return this.http.get<string[]>(url).toPromise();
  }

  getLaunchSynchronization(): boolean {
    return this.getSession().launchSynchronization;
  }

  setLaunchSynchronization(launchSynchronization: boolean): void {
    let session = this.getSession();
    session.launchSynchronization = launchSynchronization;
    this.setSession(session);
  }

  async getPasswordPoliciesText(): Promise<string> {
    const title = await this.translateService.get('SI_PASSWORD_TOOLTIP_TITLE').toPromise();
    const passwordPolicies = await this.getPasswordPolicies();
    let textPasswordPolicies = '<div>' + title + '</div>';
    return textPasswordPolicies += Object.values(passwordPolicies).map(value => '<div class="ml-2">· ' + value + '</div>').join('');
  }

  async recoverPasswordByEmail(model: RecoverPasswordModel): Promise<PasiToast> {
    const url = `/api/v1/resetPassword`;
    return this.http.put(url, model.email).toPromise();
  }

  async selectRole(authorizationId: number): Promise<void> {
    await this.updateSession(authorizationId);
    this.permissionService.init();
  }

  async updateSession(authorizationId: number): Promise<void> {
    const changeRoleObject = {authorizationId, refreshToken: this.getSession()?.refresh_token};
    const newSession = await this.updateSelectRole(changeRoleObject);
    this.afterLogin(newSession);
    window.location.href = '/';
  }

  async updateSelectRole(model: CircleAuthorizationRoleChange): Promise<Session> {
    const url = '/api/v1/circle/select';
    return this.http.put<Session>(url, model).toPromise();
  }

  getSessionEmailMasked(): string {
    return this.getEmailMasked(this.getSession()?.email);
  }

  getEmailMasked(email: string): string {
    return email ? GenericFunctionsUtil.getEmailMasked(this.emailDefaultMaskMargin, '*', email) : '';
  }

  getSessionPhoneMasked(): string {
    return this.getPhoneMasked(this.getSession()?.phone?.value);
  }

  getPhoneMasked(phone: string): string {
    return phone ? GenericFunctionsUtil.getPhoneMasked(this.phoneDefaultMaskMargin, '*', phone) : '';
  }

  getFAQs(): Promise<FaqItem[]> {
    const url = '/api/v1/faqs/private';
    return this.http.get<FaqItem[]>(url).toPromise();
  }

  getFAQsUnlogged(): Promise<FaqItem[]> {
    const url = '/api/v1/faqs';
    return this.http.get<FaqItem[]>(url).toPromise();
  }

  setResendValidationEmail(model: LoginResendEmail): Promise<void> {
    const url = '/api/v1/send-verify-email';
    return this.http.put<void>(url, model).toPromise();
  }

  ngOnDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
}
